/* Middelste kolom van de Unified Inbox — zoekbalk, filters en prospect-rijen. */
import React from "react";
import i18n from "i18next";
import { Search } from "lucide-react";
import { TAGS, tagMeta, channelMeta, substituteVars } from "./constants";

export default function ProspectList({
    tab, threads, loadingThreads, accounts, campaigns, activeId, onSelect,
    search, setSearch, tagFilter, setTagFilter, campaignFilter, setCampaignFilter,
    includeNoReply, setIncludeNoReply,
}) {
    return (
        <section className="border-r border-white/8 flex flex-col min-h-0 max-h-[45dvh] md:max-h-none" data-testid="inbox-prospects">
            <div className="p-4 border-b border-white/8">
                <h3 className="text-sm font-semibold text-white mb-3">{i18n.t("inbox.prospects_title")}</h3>
                {/* Search + filters */}
                <div className="relative mb-2">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder={i18n.t("inbox.search_placeholder")}
                        data-testid="search-prospects"
                        className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-[var(--jr-brand)]" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} data-testid="filter-tag"
                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-2 py-2 text-xs text-white outline-none">
                        <option value="">{i18n.t("inbox.all_tags")}</option>
                        {TAGS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                    <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} data-testid="filter-campaign"
                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-2 py-2 text-xs text-white outline-none">
                        <option value="">{i18n.t("inbox.all_campaigns")}</option>
                        {campaigns.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
                    </select>
                </div>
                {tab === "conversations" && (
                    <label className="mt-2 flex items-center gap-2 text-[10px] text-[var(--jr-text-3)] cursor-pointer">
                        <input type="checkbox" checked={includeNoReply} onChange={(e) => setIncludeNoReply(e.target.checked)}
                            data-testid="toggle-no-reply"
                            className="accent-[var(--jr-brand)]" />
                        {i18n.t("inbox.include_no_reply")}
                    </label>
                )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-white/5">
                {loadingThreads ? (
                    <div className="p-6 text-center text-xs text-[var(--jr-text-3)]">{i18n.t("common.loading")}</div>
                ) : threads.length === 0 ? (
                    <div className="p-8 text-center text-xs text-[var(--jr-text-3)]">{i18n.t("inbox.no_prospects")}</div>
                ) : (
                    threads.map((th) => (
                        <ProspectRow key={th.prospect_id} th={th} accounts={accounts}
                            isActive={activeId === th.prospect_id} onSelect={onSelect} />
                    ))
                )}
            </div>
        </section>
    );
}

/* Eén prospect-rij: avatar, tag-badge, snippet + account/kanaal-indicators. */
function ProspectRow({ th, accounts, isActive, onSelect }) {
    const tm = tagMeta(th.inbox_tag);
    const initials = (th.name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
    return (
        <button onClick={() => onSelect(th.prospect_id)}
            data-testid={`prospect-${th.prospect_id}`}
            className={`w-full text-left px-4 py-3 transition-colors ${isActive ? "bg-[var(--jr-brand)]/[0.08] border-l-2 border-[var(--jr-brand)]" : "border-l-2 border-transparent hover:bg-white/[0.03]"}`}>
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[var(--jr-surface-2)] grid place-items-center text-[11px] font-mono uppercase text-[var(--jr-text-2)] shrink-0">
                    {initials}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-white truncate">{th.name || "Unknown"}</span>
                        <span className={`text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border ${tm.cls}`}>
                            {tm.label}
                        </span>
                    </div>
                    <p className="text-xs text-[var(--jr-text-2)] line-clamp-1 mt-0.5">
                        {substituteVars(th.last_message?.snippet, { name: th.name, company_name: th.company_name }) || "—"}
                    </p>
                    <div className="flex items-center justify-between mt-1 text-[10px] text-[var(--jr-text-3)]">
                        <span className="truncate flex items-center gap-1.5">
                            {accounts.length > 1 && th.on_behalf_of_user_name && (() => {
                                const acc = accounts.find((a) => a.id === th.on_behalf_of_user_id);
                                const accInit = (th.on_behalf_of_user_name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
                                return acc?.avatar_url ? (
                                    <img src={acc.avatar_url} alt={th.on_behalf_of_user_name}
                                        title={`Account: ${th.on_behalf_of_user_name}`}
                                        data-testid={`thread-account-${th.prospect_id}`}
                                        className="w-3.5 h-3.5 rounded-full object-cover shrink-0" />
                                ) : (
                                    <span title={`Account: ${th.on_behalf_of_user_name}`}
                                        data-testid={`thread-account-${th.prospect_id}`}
                                        className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full shrink-0 bg-[var(--jr-brand)]/20 text-[var(--jr-brand)] text-[7px] font-mono font-bold">
                                        {accInit}
                                    </span>
                                );
                            })()}
                            {th.last_message?.channel && (() => {
                                const cm = channelMeta(th.last_message.channel);
                                const CIcon = cm.Icon;
                                return (
                                    <span title={i18n.t("inbox.last_via", { channel: cm.label })}
                                        data-testid={`thread-last-channel-${th.prospect_id}`}
                                        className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full shrink-0"
                                        style={{ background: cm.tint + "22", color: cm.tint }}>
                                        <CIcon size={8} />
                                    </span>
                                );
                            })()}
                            <span className="truncate">{[th.title, th.company_name].filter(Boolean).join(" · ") || "—"}</span>
                        </span>
                        <span>
                            {th.last_message?.status === "replied" && <span className="text-[#22c55e]">replied</span>}
                            {th.last_message?.status === "sent" && <span>sent</span>}
                            {th.last_message?.status === "scheduled" && <span className="text-[var(--jr-info)]">scheduled</span>}
                            {(th.last_message?.status === "draft" || th.last_message?.status === "pending_approval") && <span className="text-[var(--jr-amber)]">{th.last_message?.status === "pending_approval" ? "pending" : "draft"}</span>}
                        </span>
                    </div>
                </div>
            </div>
        </button>
    );
}
